import React, { PropsWithChildren, useState } from 'react';

import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ApiError } from 'shared/api';
import { isServerUnavailable } from 'shared/lib/isServerUnavailable';
import { notifyServerError } from 'shared/lib/notifyServerError';

const BaseQueryClientProvider = ({ children }: PropsWithChildren) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error: unknown) => {
            // NOTE: Only handler ApiError that is not handled by their own error handler
            if (error instanceof ApiError) {
              alert(error.message);
            }
          },
        }),

        // NOTE: 뮤테이션의 5xx 는 토스트로만 알린다 (아래 throwOnError 주석 참고).
        // meta 에 SKIP_SERVER_ERROR_TOAST 를 넣은 뮤테이션은 제외 — 로그아웃처럼
        // 실패해도 자체적으로 성공 처리하는 경우 에러 토스트가 오히려 혼란을 준다.
        mutationCache: new MutationCache({
          onError: (error, _variables, _context, mutation) => notifyServerError(error, mutation.options.meta),
        }),

        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,

            // 기본값(3회 / 1·2·4초)이면 여러 쿼리의 재시도가 겹쳐 에러 확정까지
            // 30초 넘게 걸린다. 배포 다운타임이 약 35초라 안내 화면이 뜰 때쯤엔
            // 이미 서버가 돌아와 있어 아무도 못 보는 화면이 된다(실측 확인).
            // 2회로 줄여 1초 안팎에 확정시킨다 — 일시적 끊김은 여전히 흡수하고,
            // 안내 화면에는 재시도 버튼이 있다.
            retry: 2,
            retryDelay: 400,

            // NOTE: 서버에 닿지 못하면 에러 바운더리에서 처리.
            // 조회 실패는 어차피 화면을 그릴 데이터가 없으므로 전체 안내 화면이 맞다.
            throwOnError: (error) => isServerUnavailable(error),
          },

          mutations: {
            // NOTE: 뮤테이션은 에러 바운더리로 넘기지 않는다.
            // 전체 화면이 교체되면 작성 중이던 가계약·결제 폼 입력값이 사라지기 때문.
            // 대신 mutationCache.onError 의 토스트로 알리고 입력값은 그대로 둔다.
            throwOnError: false,
          },
        },
      }),
  );

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
};

export default BaseQueryClientProvider;
